import * as React from 'react';
import { Link } from "react-router-dom";
import Button from '@mui/joy/Button';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Stack from '@mui/joy/Stack';

// GiveModal
// -----------------------------------------------------------------------------
// Trigger link that opens a "ways to give" dialog. Public prop contract
// (consumed by Navigator, Footer, Home) is unchanged:
//   - text      : node    label rendered inside the trigger link
//   - linkClass : string  className applied to the trigger link
//
// PRESERVED functional contracts (do not change):
//   - Realm URL https://onrealm.org/nhvineyard/give/now
//   - PayPal form action https://www.paypal.com/ncp/payment/3EBSAFR7WTMG6
//     method="post" target="_blank", the pp-3EBSAFR7WTMG6 input class,
//     and the PayPal branding images.
// -----------------------------------------------------------------------------

// Give-button surface. Prefers the Joy accent token and falls back to the mapped
// PNC orange when the CSS var is unavailable. Dark ink keeps AA contrast on
// accent 500 (~6.5:1); no Joy token maps to #1A1A1A.
const GIVE_BUTTON_BACKGROUND = 'var(--joy-palette-accent-500, #F58025)';
const GIVE_BUTTON_TEXT = '#1A1A1A';

// Presentational sub-block: the PayPal hosted-button form. `minWidth` lets the
// desktop / mobile layouts size the button without duplicating the markup.
const PayPalForm = ({ minWidth }) => (
    <form
        action="https://www.paypal.com/ncp/payment/3EBSAFR7WTMG6"
        method="post"
        target="_blank"
        style={{ display: 'inline-grid', justifyItems: 'center', alignContent: 'start', gap: '0.5rem' }}
    >
        <input
            className="pp-3EBSAFR7WTMG6"
            type="submit"
            value="Give"
            style={{
                textAlign: 'center',
                border: 'none',
                borderRadius: '0.5rem',
                minWidth,
                padding: '0.2rem',
                height: '3.625rem',
                fontWeight: 'bold',
                backgroundColor: GIVE_BUTTON_BACKGROUND,
                color: GIVE_BUTTON_TEXT,
                fontFamily: '"HelveticaNeue",Arial,sans-serif',
                fontSize: '1rem',
                lineHeight: '1.25rem',
                cursor: 'pointer',
            }}
        />
        <img src='https://www.paypalobjects.com/images/Debit_Credit.svg' alt="cards" />
        <section> Powered by <img
            src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg"
            alt="paypal"
            style={{ height: '0.875rem', verticalAlign: 'middle' }} />
        </section>
    </form>
);

const GiveModal = (data) => {
    const [open, setOpen] = React.useState(false);
    const text = data.text;
    const linkClass = data.linkClass;

    // Presentational sub-block: the Realm "Give" button.
    const realmButton = (minWidth) => (
        <Link to="https://onrealm.org/nhvineyard/give/now" target='_blank' rel='noopener'>
            <Button
                color="primary"
                sx={{
                    minWidth,
                    padding: '0.2rem',
                    height: '3.625rem',
                    borderRadius: 'md',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                }}
            >
                Give
            </Button>
        </Link>
    );

    return (
        <React.Fragment>
            <Link
                className={linkClass}
                onClick={() => setOpen(true)}
                to="#"
            >
                {text}
            </Link>
            <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog
                    sx={{
                        bgcolor: 'background.surface',
                        borderColor: 'neutral.200',
                        borderRadius: 'lg',
                        boxShadow: 'lg',
                    }}
                >
                    <ModalClose />
                    <DialogTitle sx={{ justifyContent: 'center', color: 'text.primary' }}>
                        Ways to support NHV
                    </DialogTitle>
                    <DialogContent sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        Thank you for your generosity. Choose whichever option works best for you.
                    </DialogContent>

                    {/* Desktop layout */}
                    <Stack spacing={2} sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center', mt: 1 }}>
                        <Divider>Give via Realm</Divider>
                        {realmButton('20.625rem')}
                        <Divider>Give via PayPal</Divider>
                        <PayPalForm minWidth="20.625rem" />
                    </Stack>

                    {/* Mobile layout */}
                    <Stack spacing={2} sx={{ display: { md: 'none', xs: 'flex' }, alignItems: 'center', mt: 1 }}>
                        <Divider>Give via Realm</Divider>
                        {realmButton('18.625rem')}
                        <Divider>Give via PayPal</Divider>
                        <PayPalForm minWidth="18.625rem" />
                    </Stack>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    );
};

export default GiveModal;
